import { useStyles } from './styles'
import { TourTextProps } from '../tour.typings'
import TourTooltip from '../tourTooltip/tourTooltip'
import React, { memo, ReactNode, useCallback } from 'react'
import Button from '@mui/material/Button'
import { useDispatch, useSelector } from 'react-redux'
import { closeTour, gotoNextStep, gotoPreviousStep } from '../../../reducer/tour.reducer'
import { selectCurrentStep, selectTourSteps } from '../../../selectors/tour'

/**
 * Step copy in tourStepsMap carries inline colours and sizes written for a white bubble.
 * Replace each `style` with a semantic class so the theme decides the colours:
 * blue accents become `.tour-accent`, enlarged paragraphs become `.tour-title`.
 */
export function normalizeTourHtml(html: string): string {
    return html.replace(/\s*style="([^"]*)"/gi, (_match, style: string) => {
        const classes = [
            /color:\s*#1e90ff/i.test(style) ? 'tour-accent' : null,
            /font-size:\s*1\.2em/i.test(style) ? 'tour-title' : null
        ].filter(Boolean)
        return classes.length ? ` class="${classes.join(' ')}"` : ''
    })
}

const TourTexts: React.FC<TourTextProps> = ({ elements }) => {
    const dispatch = useDispatch()
    const { classes } = useStyles()
    const steps = useSelector(selectTourSteps)
    const currentStepNumber = useSelector(selectCurrentStep)
    const isLast = currentStepNumber === steps.length - 1

    const onNextClick = useCallback(() => {
        if (isLast) {
            dispatch(closeTour())
            return
        }
        dispatch(gotoNextStep())
    }, [dispatch, isLast])
    const onPreviousClick = useCallback(() => dispatch(gotoPreviousStep()), [dispatch])
    const onSkipClick = useCallback(() => dispatch(closeTour()), [dispatch])

    return (
        <>
            {elements.map((ele, i) => {
                const { coordinates: { topLeft, topRight, bottomLeft }, textPosition, text } = ele
                const width = topRight.x - topLeft.x
                const height = bottomLeft.y - topLeft.y

                const html: ReactNode = (
                    <>
                        <div dangerouslySetInnerHTML={{ __html: normalizeTourHtml(text) }} className={classes.tooltipContent} />
                        <div className={classes.tooltipBottomBar}>
                            <Button onClick={onSkipClick} className={classes.skipButton} variant="text" color="inherit" size="small">
                                Skip
                            </Button>
                            {steps.length > 1 ? (
                                <span className={classes.stepCounter}>
                                    {currentStepNumber + 1} of {steps.length}
                                </span>
                            ) : null}
                            <div className={classes.actionButtonsContainer}>
                                {currentStepNumber > 0 ? (
                                    <Button onClick={onPreviousClick} className={classes.actionButton} variant="outlined" color="inherit" size="small">
                                        Back
                                    </Button>
                                ) : null}
                                <Button onClick={onNextClick} className={classes.actionButton} disableElevation variant="contained" size="small">
                                    {isLast ? 'Finish' : 'Next'}
                                </Button>
                            </div>
                        </div>
                    </>
                )
                return (
                    <TourTooltip key={ele.selector ? `${ele.selector}-${i}` : i} placement={textPosition} title={html}>
                        <div
                            className={classes.anchor}
                            style={{ left: topLeft.x, top: topLeft.y, width: `${width}px`, height: `${height}px` }}
                        />
                    </TourTooltip>
                )
            })}
        </>
    )
}

export default memo(TourTexts)
